// =============================================================================
// Task Types
// =============================================================================
export const TASK_STATUSES = [
    'draft',
    'ready',
    'queued',
    'delegated',
    'in_progress',
    'completed',
    'failed',
    'cancelled',
];
/** Runtime type guard for TaskStatus values from untrusted sources (e.g. database rows). */
export function isTaskStatus(value) {
    return typeof value === 'string' && TASK_STATUSES.includes(value);
}
export const TASK_MODES = ['task', 'conversation'];
/** Runtime type guard for TaskMode values from untrusted sources (e.g. database rows). */
export function isTaskMode(value) {
    return typeof value === 'string' && TASK_MODES.includes(value);
}
/**
 * Tracks where the task runner is during async execution.
 * Persisted to the task record so stuck-task recovery knows WHERE execution stalled.
 */
export const TASK_EXECUTION_STEPS = [
    'node_selection',
    'node_provisioning',
    'node_agent_ready',
    'workspace_creation',
    'workspace_dispatch',
    'workspace_ready',
    'attachment_transfer',
    'agent_session',
    'running',
    'awaiting_followup',
];
export function isTaskExecutionStep(value) {
    return typeof value === 'string' && TASK_EXECUTION_STEPS.includes(value);
}
/** Human-readable labels for each execution step (TDF-8). */
export const EXECUTION_STEP_LABELS = {
    node_selection: 'Finding a server...',
    node_provisioning: 'Setting up a new server...',
    node_agent_ready: 'Waiting for server to start...',
    workspace_creation: 'Creating workspace...',
    workspace_dispatch: 'Starting workspace on server...',
    workspace_ready: 'Setting up development environment...',
    attachment_transfer: 'Uploading attachments to workspace...',
    agent_session: 'Starting AI agent...',
    running: 'Agent is working...',
    awaiting_followup: 'Waiting for follow-up...',
};
/** Ordered index for execution step progress — derived from TASK_EXECUTION_STEPS array position (TDF-8). */
export const EXECUTION_STEP_ORDER = Object.fromEntries(TASK_EXECUTION_STEPS.map((step, i) => [step, i]));
export const COMPLETION_EVIDENCE_VERIFICATION_KINDS = [
    'test',
    'staging',
    'manual',
    'ci',
    'other',
];
const COMPLETION_EVIDENCE_LIMITS = {
    maxTestsRun: 25,
    maxVerifications: 25,
    maxCommandLength: 500,
    maxDetailLength: 2000,
    maxDescriptionLength: 2000,
    maxEvidenceLength: 2000,
    maxPrUrlLength: 500,
    maxNotesLength: 4000,
};
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function optionalTrimmedString(value, field, maxLength) {
    if (value === undefined)
        return { ok: true };
    if (typeof value !== 'string') {
        return { ok: false, error: `${field} must be a string` };
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return { ok: false, error: `${field} must not be empty` };
    }
    if (trimmed.length > maxLength) {
        return { ok: false, error: `${field} must be ${maxLength} characters or fewer` };
    }
    return { ok: true, value: trimmed };
}
function validateEvidenceArray(value, field, maxItems, parseItem) {
    if (!Array.isArray(value)) {
        return { ok: false, error: `${field} must be an array` };
    }
    if (value.length > maxItems) {
        return { ok: false, error: `${field} must contain ${maxItems} items or fewer` };
    }
    const parsed = [];
    for (const [index, item] of value.entries()) {
        if (!isRecord(item)) {
            return { ok: false, error: `${field}[${index}] must be an object` };
        }
        const result = parseItem(item, index);
        if (!result.ok)
            return result;
        parsed.push(result.value);
    }
    return { ok: true, value: parsed };
}
function requiredEvidenceString(value, field, maxLength) {
    const result = optionalTrimmedString(value, field, maxLength);
    if (!result.ok)
        return result;
    if (!result.value) {
        return { ok: false, error: `${field} must not be empty` };
    }
    return { ok: true, value: result.value };
}
function validateCompletionTestRun(item, index) {
    const command = requiredEvidenceString(item.command, `evidence.testsRun[${index}].command`, COMPLETION_EVIDENCE_LIMITS.maxCommandLength);
    if (!command.ok)
        return command;
    if (typeof item.passed !== 'boolean') {
        return { ok: false, error: `evidence.testsRun[${index}].passed must be a boolean` };
    }
    const detail = optionalTrimmedString(item.detail, `evidence.testsRun[${index}].detail`, COMPLETION_EVIDENCE_LIMITS.maxDetailLength);
    if (!detail.ok)
        return detail;
    return {
        ok: true,
        value: {
            command: command.value,
            passed: item.passed,
            ...(detail.value ? { detail: detail.value } : {}),
        },
    };
}
function validateCompletionVerification(item, index) {
    if (typeof item.kind !== 'string' ||
        !COMPLETION_EVIDENCE_VERIFICATION_KINDS.includes(item.kind)) {
        return {
            ok: false,
            error: `evidence.verifications[${index}].kind must be one of: ${COMPLETION_EVIDENCE_VERIFICATION_KINDS.join(', ')}`,
        };
    }
    const description = requiredEvidenceString(item.description, `evidence.verifications[${index}].description`, COMPLETION_EVIDENCE_LIMITS.maxDescriptionLength);
    if (!description.ok)
        return description;
    const evidence = optionalTrimmedString(item.evidence, `evidence.verifications[${index}].evidence`, COMPLETION_EVIDENCE_LIMITS.maxEvidenceLength);
    if (!evidence.ok)
        return evidence;
    return {
        ok: true,
        value: {
            kind: item.kind,
            description: description.value,
            ...(evidence.value ? { evidence: evidence.value } : {}),
        },
    };
}
export function validateCompletionEvidence(value) {
    if (!isRecord(value)) {
        return { ok: false, error: 'evidence must be an object' };
    }
    const output = {};
    let populatedFields = 0;
    if (value.testsRun !== undefined) {
        const testsRun = validateEvidenceArray(value.testsRun, 'evidence.testsRun', COMPLETION_EVIDENCE_LIMITS.maxTestsRun, validateCompletionTestRun);
        if (!testsRun.ok)
            return testsRun;
        output.testsRun = testsRun.value;
        populatedFields += 1;
    }
    if (value.verifications !== undefined) {
        const verifications = validateEvidenceArray(value.verifications, 'evidence.verifications', COMPLETION_EVIDENCE_LIMITS.maxVerifications, validateCompletionVerification);
        if (!verifications.ok)
            return verifications;
        output.verifications = verifications.value;
        populatedFields += 1;
    }
    const prUrl = optionalTrimmedString(value.prUrl, 'evidence.prUrl', COMPLETION_EVIDENCE_LIMITS.maxPrUrlLength);
    if (!prUrl.ok)
        return prUrl;
    if (prUrl.value) {
        output.prUrl = prUrl.value;
        populatedFields += 1;
    }
    const notes = optionalTrimmedString(value.notes, 'evidence.notes', COMPLETION_EVIDENCE_LIMITS.maxNotesLength);
    if (!notes.ok)
        return notes;
    if (notes.value) {
        output.notes = notes.value;
        populatedFields += 1;
    }
    if (populatedFields === 0) {
        return { ok: false, error: 'evidence must include at least one supported field' };
    }
    return { ok: true, value: output };
}
export function parseCompletionEvidenceJson(raw) {
    if (!raw)
        return null;
    try {
        const parsed = JSON.parse(raw);
        const validation = validateCompletionEvidence(parsed);
        return validation.ok ? validation.value : null;
    }
    catch {
        return null;
    }
}
/** Default attachment size/count limits — all overridable via env vars. */
export const ATTACHMENT_DEFAULTS = {
    /** Maximum file size per attachment in bytes (default: 50MB). Env: ATTACHMENT_UPLOAD_MAX_BYTES */
    UPLOAD_MAX_BYTES: 50 * 1024 * 1024,
    /** Maximum total batch size in bytes (default: 200MB). Env: ATTACHMENT_UPLOAD_BATCH_MAX_BYTES */
    UPLOAD_BATCH_MAX_BYTES: 200 * 1024 * 1024,
    /** Maximum number of files per task (default: 20). Env: ATTACHMENT_MAX_FILES */
    MAX_FILES: 20,
    /** Presigned URL expiry in seconds (default: 900 = 15min). Env: ATTACHMENT_PRESIGN_EXPIRY_SECONDS */
    PRESIGN_EXPIRY_SECONDS: 900,
};
/** Regex for safe filenames — matches the VM agent pattern. */
export const SAFE_FILENAME_REGEX = /^[a-zA-Z0-9._\- ]+$/;
//# sourceMappingURL=task.js.map